import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Activity,
  CircleHelp,
  Download,
  Globe,
  LayoutGrid,
  MonitorSmartphone,
  Package,
  Settings2,
  Sun,
  Tablet,
  TriangleAlert,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

const SECTIONS = [
  { id: "basics", title: "איך המערכת עובדת", icon: CircleHelp },
  { id: "dashboard", title: "עמוד המכשירים", icon: LayoutGrid },
  { id: "device-overview", title: "מכשיר · סקירה", icon: Tablet },
  { id: "device-display", title: "מכשיר · תצוגה", icon: Globe },
  { id: "device-screen", title: "מכשיר · מסך", icon: MonitorSmartphone },
  { id: "device-settings", title: "מכשיר · הגדרות", icon: Settings2 },
  { id: "device-updates", title: "מכשיר · עדכונים", icon: Download },
  { id: "device-logs", title: "מכשיר · יומן", icon: Activity },
  { id: "builds", title: "עמוד הגרסאות", icon: Package },
  { id: "appearance", title: "מראה וערכת נושא", icon: Sun },
  { id: "troubleshooting", title: "פתרון בעיות", icon: TriangleAlert },
]

export function HelpPage() {
  const { hash } = useLocation()

  // ניווט פנימי של React Router לא גולל לעוגן מעצמו - רק טעינה ישירה של ה-URL עושה זאת
  React.useEffect(() => {
    if (!hash) return
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [hash])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">מדריך למשתמש</h1>
        <p className="text-sm text-muted-foreground">
          מה כל מסך, כרטיס וכפתור במערכת עושים - ומה לעשות כשמשהו לא מתנהג כצפוי.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav className="lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardContent className="p-2">
              <ul className="space-y-0.5 text-sm">
                {SECTIONS.map(({ id, title, icon: Icon }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Icon className="size-3.5 shrink-0" />
                      <span className="truncate">{title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </nav>

        <div className="min-w-0 space-y-6">
          <Section id="basics" title="איך המערכת עובדת" icon={CircleHelp}>
            <p>
              המערכת מנהלת מרחוק את הטאבלטים שמשמשים לאיסוף מתנות באירועים. כל טאבלט מריץ אפליקציה
              במצב קיוסק, שמציגה את דף המתנות בתוך דפדפן פנימי (WebView), ומחזיקה חיבור פתוח לשרת הזה.
            </p>
            <Term term="מכשיר">
              טאבלט שהתחבר לשרת לפחות פעם אחת. הוא מזוהה לפי מזהה קבוע של המכשיר, כך שגם אחרי
              איתחול או החלפת רשת הוא חוזר לאותה כרטיסייה ולא נוצר כפול.
            </Term>
            <Term term="מחובר / לא מחובר">
              מחובר = יש כרגע חיבור חי בין הטאבלט לשרת, ולכן אפשר לשלוח אליו פקודות. כשהוא לא מחובר,
              כפתורי הפעולה מנוטרלים - השרת לא שומר פקודות בתור ואינו שולח אותן מאוחר יותר.
            </Term>
            <Term term="פקודה ותגובה">
              כל לחיצה על כפתור פעולה שולחת פקודה לטאבלט ומחכה לתשובה שלו עד 15 שניות. ההצלחה או
              הכישלון מוצגים כהודעה קופצת, והכול נרשם ביומן האירועים של המכשיר.
            </Term>
            <Term term="עדכון חי">
              העמודים מתעדכנים מעצמם כשמכשיר מתחבר, מתנתק או מדווח סטטוס - אין צורך לרענן את הדפדפן.
            </Term>
          </Section>

          <Section id="dashboard" title="עמוד המכשירים" icon={LayoutGrid}>
            <p>
              המסך הראשי. כרטיס לכל טאבלט שהמערכת מכירה, עם מצב החיבור, אחוז הסוללה, דגם המכשיר
              וגרסת האפליקציה. לחיצה על כרטיס פותחת את עמוד המכשיר.
            </p>
            <Item name="שורת החיפוש">
              מסננת לפי שם, מזהה מכשיר, דגם או הערות שכתבת. שימושי כשיש הרבה טאבלטים באירוע.
            </Item>
            <Item name="רענן">
              טוען מחדש את הרשימה מהשרת (מה שהשרת יודע), בלי לפנות לטאבלטים עצמם.
            </Item>
            <Item name="עדכן סטטוס מכולם">
              פונה לכל הטאבלטים המחוברים ומבקש מהם דיווח סטטוס טרי - סוללה, זיכרון, אחסון וגרסה.
              זה מה שכדאי ללחוץ לפני אירוע כדי לוודא שכולם חיים וטעונים.
            </Item>
            <Item name="שורת הזמן שבכרטיס">
              במכשיר מחובר מוצג מתי הוא התחבר; במכשיר מנותק - מתי נראה לאחרונה.
            </Item>
          </Section>

          <Section id="device-overview" title="עמוד מכשיר · לשונית סקירה" icon={Tablet}>
            <p>
              עמוד המכשיר מחולק ללשוניות כדי שלא הכול ייפול עליך במסך אחד. לשונית הסקירה עונה על
              השאלה "מה מצב הטאבלט הזה עכשיו".
            </p>
            <Item name="פעולות מהירות · רענון סטטוס">
              מבקש מהטאבלט דיווח מצב עדכני (סוללה, זיכרון, אחסון, גרסה). פעולה בטוחה לחלוטין.
            </Item>
            <Item name="פעולות מהירות · Ping">
              בדיקת חיים פשוטה: האם הטאבלט עונה, וכמה מהר. לא משנה כלום במכשיר.
            </Item>
            <Item name="פעולות מהירות · רענון WebView">
              טוען מחדש את הדף שמוצג על מסך הטאבלט. עוזר כשהדף נתקע או מציג שגיאה.
              <Warn>איסוף מתנה שנמצא באמצע המסך ייקטע.</Warn>
            </Item>
            <Item name="פעולות מהירות · איתחול">
              מכבה ומדליק את הטאבלט עצמו. הוא יהיה מנותק לכמה דקות עד שיחזור.
              <Warn>שמור לאחרון - נסה קודם רענון WebView או הפעלה מחדש של האפליקציה.</Warn>
            </Item>
            <Item name="מוצג כעת על המסך">
              הכתובת שמוצגת ממש עכשיו בדפדפן הפנימי של הטאבלט, נשאלת מהמכשיר אוטומטית כל 30 שניות.
              כך רואים שהטאבלט באמת עומד על דף המתנות הנכון ולא נדד לאנשהו.
              <br />
              <span className="text-muted-foreground">
                "אין עמוד טעון" = הטאבלט ענה, אבל אין דף פתוח בדפדפן הפנימי שלו.
              </span>
            </Item>
            <Item name="פרטי מכשיר">
              תווית והערות שאתה כותב, ונשמרות בשרת בלבד. התווית מחליפה את השם שהטאבלט מדווח על עצמו,
              כדי שתוכל לקרוא לו למשל "כניסה - אולם א'". ההערות הן לך.
            </Item>
            <Item name="סטטוס אחרון">
              תמונת המצב האחרונה שהתקבלה מהטאבלט: סוללה, טעינה, זיכרון, אחסון, זמן פעילות, מצב חיסכון
              בסוללה ומצב הקיוסק. אם המספרים נראים ישנים - לחץ "רענון סטטוס".
            </Item>
            <Item name="שחזור אוטומטי לקיוסק">
              הטאבלט חוזר למצב קיוסק בעצמו אחרי כמה דקות, גם אם כיבו את המתג, יצאו מהאפליקציה או
              נשארו במסך ההגדרות - וגם אחרי אתחול או ניתוק חשמל. כאן רואים אם הקיוסק תקין כרגע, ומה
              שבור אם לא.{" "}
              <span className="text-muted-foreground">
                "החזר לקיוסק עכשיו" מדלג על ההמתנה. כדי להשאיר טאבלט מחוץ לקיוסק לצמיתות (למשל בזמן
                תחזוקה) צריך לכבות את "שחזור אוטומטי למצב קיוסק" בלשונית ההגדרות - כיבוי מתג הקיוסק
                לבדו לא יספיק.
              </span>
            </Item>
          </Section>

          <Section id="device-display" title="עמוד מכשיר · לשונית תצוגה" icon={Globe}>
            <p>מה הטאבלט מציג, ולאן מותר לו לגלוש.</p>
            <Item name="כתובת אתחול (WebView)">
              הכתובת שהטאבלט טוען כשהאפליקציה עולה - כלומר דף האירוע שלו. שינוי כאן קובע לאיזה אירוע
              הטאבלט משויך. שים לב שזו לא בהכרח הכתובת המוצגת כרגע (ראה "מוצג כעת על המסך").
            </Item>
            <Item name="רשימת דומיינים מורשים">
              רשימת ההיתר של הדפדפן הפנימי. הטאבלט יוכל להיכנס רק לכתובות מהדומיינים האלה, כדי שאורח
              סקרן לא יגלוש למקומות אחרים. אם תשמיט כאן את הדומיין של דף המתנות - הדף לא ייטען.
            </Item>
          </Section>

          <Section id="device-screen" title="עמוד מכשיר · לשונית מסך" icon={MonitorSmartphone}>
            <p>
              לראות מה באמת מוצג על הטאבלט - גם צילום בודד וגם שידור חי. שימושי כשמישהו מדווח
              ש"משהו מוזר על המסך" ואתה לא במקום.
            </p>
            <Item name="צלם עכשיו">
              מצלם את מה שמוצג על הטאבלט ברגע זה. אם מוצגת פרסומת היא נסגרת אוטומטית לפני הצילום,
              כדי שתראה את התוכן ולא את הפרסומת. אפשר להוריד את התמונה.
              <br />
              <span className="text-muted-foreground">
                הפרסומת לא חוזרת אחרי הצילום - היא נסגרה באמת, בדיוק כמו אורח שנגע במסך.
              </span>
            </Item>
            <Item name="שיקוף מסך חי · חלון האפליקציה">
              זרם תמונות חי ממה שהאפליקציה מציירת. לא דורש שום הרשאה ולא צריך אף אחד ליד הטאבלט,
              ולכן זה המצב לשימוש יומיומי. ברירת מחדל 8 תמונות בשנייה.
            </Item>
            <Item name="שיקוף מסך חי · מסך מלא אמיתי">
              מצלם את המסך כולו - שורת סטטוס, מקלדת ואפליקציות אחרות, לא רק את האפליקציה שלנו.
              ברירת מחדל 15 תמונות בשנייה.
              <Warn>
                אנדרואיד לא מאפשר לאשר את זה מרחוק, גם לא לאפליקציה שמותקנת כבעלים של המכשיר -
                מישהו חייב ללחוץ "אשר" פיזית על הטאבלט. באנדרואיד 14 ומעלה האישור תקף לשידור אחד
                בלבד, וצריך לאשר שוב בפעם הבאה. אם אין מי שיאשר במקום, השתמש ב"חלון האפליקציה".
              </Warn>
            </Item>
            <Item name="בקש אישור מהטאבלט">
              מקפיץ את דיאלוג האישור של אנדרואיד על מסך הטאבלט. מיד עם האישור השיקוף מתחיל לבד -
              אין צורך ללחוץ "הפעל" בנפרד (וזה גם נמנע מכך שהאישור יפוג בינתיים).
            </Item>
            <Item name="פריימים בשנייה">
              1 עד 30. יותר תמונות = תחושה חלקה יותר, אבל גם יותר עומס על הטאבלט ויותר תעבורה.
              אם הרשת לא עומדת בקצב, המכשיר פשוט מוריד את הקצב מעצמו - הוא זורק תמונות ישנות
              במקום לצבור אותן, כך שמה שאתה רואה תמיד עדכני.
            </Item>
            <Item name="סגור פרסומת">
              סוגר פרסומת שמוצגת כרגע על הטאבלט, בלי קשר לשיקוף. עובד גם בזמן שידור חי - התמונה
              הבאה כבר תראה את התוכן.
            </Item>
            <Item name="מה נראה בצילום ובשיקוף">
              מה שמוצג על הטאבלט באותו רגע - כולל מסך ההגדרות, ניהול הדומיינים או צפיית
              היומן, ולא רק דף המתנות.
              <br />
              <span className="text-muted-foreground">
                במסך ההגדרות הסיסמאות מוצגות כנקודות, ולכן הוא משוקף כרגיל. אם מישהו לוחץ
                על "הצג סיסמה" בטאבלט, אנדרואיד מחשיך את השיקוף מעצמו עד שהיא תוסתר שוב -
                כך שסיסמה גלויה לעולם לא משודרת.
              </span>
            </Item>
            <Item name="השיקוף נכבה לבד">
              כשאתה עוזב את הלשונית או את עמוד המכשיר, השיקוף נעצר מיד. גם אם תסגור את הדפדפן,
              תאבד רשת או המחשב ייכבה - הטאבלט מפסיק לשדר תוך כחצי דקה מעצמו, כדי שלא יישאר
              משדר לחינם ויבזבז סוללה.
            </Item>
          </Section>

          <Section id="device-settings" title="עמוד מכשיר · לשונית הגדרות" icon={Settings2}>
            <Item name="הגדרות האפליקציה">
              ההגדרות של האפליקציה על הטאבלט: אבטחה וסיסמאות, מקלדת, רענון אוטומטי, פרסומות וחיסכון
              בסוללה. הרשימה נבנית ממה שהטאבלט עצמו מדווח, ולכן טאבלט עם גרסה ישנה יציג פחות שדות.
              <br />
              שדה שנגעת בו מסומן כשינוי לא שמור, ורק שדות שהשתנו נשלחים בפועל - כדי לא לדרוס הגדרות
              שמישהו שינה על הטאבלט בינתיים.
            </Item>
            <Item name="רענון אוטומטי · בהיעדר נגיעה">
              "רענון אוטומטי בהיעדר נגיעה (שניות)" הוא הזמן שהטאבלט ימתין בלי שאיש נגע במסך לפני
              שיטען מחדש את הדף מההתחלה. 0 מבטל את הרענון הזה לגמרי. הספירה מתאפסת בכל נגיעה.
              <br />
              "התראה על המסך לפני הרענון (שניות)" קובע כמה שניות לפני הרענון תוצג לאורח הודעה
              שסופרת לאחור - "הדף יתרענן בעוד 10 שניות". נגיעה במסך בזמן ההתראה מבטלת את הרענון
              ומתחילה את הספירה מחדש. 0 מבטל את ההתראה, והרענון פשוט קורה.
              <br />
              <span className="text-muted-foreground">
                ההתראה ארוכה מהמרווח עצמו מתקצרת אליו - מרווח של 5 שניות לא יציג התראה של 10.
                בטאבלטים שעודכנו מגרסה ישנה, ההגדרה שהייתה בדקות הומרה אוטומטית לשניות.
              </span>
            </Item>
            <Item name="רענון אוטומטי · אישור לרענון במשיכה">
              כשמשיכה לרענון מופעלת בטאבלט, "אישור לפני רענון במשיכה" קובע מה יקרה כשאורח מושך
              את המסך למטה. כבוי (ברירת המחדל) - הדף מתרענן מיד, כמו היום. דלוק - תוצג הודעה
              במרכז המסך שסופרת לאחור, עם כפתורי "רענן כעת" ו-"בטל".
              <br />
              אם אף אחד לא לוחץ, הספירה מסתיימת והדף מתרענן - כדי שאורח שמשך בטעות והלך לא ישאיר
              את הטאבלט תקוע מאחורי חלונית. את אורך הספירה קובעים ב"ספירה לאחור באישור הרענון
              במשיכה".
              <Warn>
                ההגדרה משפיעה רק כשמשיכה לרענון עצמה דלוקה (בקבוצת "סיסמאות והגנה").
              </Warn>
            </Item>
            <Item name="רענון אוטומטי · זמנים קבועים">
              "רענון מתוזמן" הם עד חמש שעות ביממה שבהן יתבצע רענון כפוי - ללא התראה וללא קשר לנגיעה
              במסך. מיועד לשעות שבהן בטוח אין אורחים מול הטאבלט.
            </Item>
            <Item name="שמור / שמור + הפעל מחדש">
              חלק מההגדרות נכנסות לתוקף מיד, וחלק נקראות רק כשהאפליקציה עולה. אם בין השינויים יש
              כאלה מהסוג השני, תופיע הודעה - ואז "שמור + הפעל מחדש" מחיל אותן עכשיו.
            </Item>
            <Item name="הפעל מחדש">
              סוגר ופותח את האפליקציה על הטאבלט. הטאבלט עצמו לא מאותחל. זה גם הפתרון כשמופיעה השגיאה
              "מסך האפליקציה אינו רשום לשליטה מרחוק".
            </Item>
            <Item name="הצג סיסמאות">
              שדות סיסמה מוסתרים כברירת מחדל כדי שלא ייחשפו כשמישהו מסתכל לך על המסך.
            </Item>
            <Item name="הגדרות שליטה מרחוק">
              משנה את כתובת השרת ואת סוג החיבור שהטאבלט עצמו מתחבר אליהם.
              <Warn>
                זו ההגדרה המסוכנת ביותר במערכת: כתובת שגויה כאן פירושה טאבלט שלא יחזור להתחבר, ותיקון
                ידני עליו במקום.
              </Warn>
            </Item>
            <Item name="אזור מסוכן · מחיקת מכשיר">
              מוחק את המכשיר, את יומן האירועים ואת היסטוריית הסטטוס שלו מהשרת. אפשרי רק כשהוא לא
              מחובר. אם הטאבלט יתחבר שוב בעתיד - הוא יופיע מחדש כמכשיר חדש, בלי היסטוריה ובלי התווית
              שנתת לו.
            </Item>
          </Section>

          <Section id="device-updates" title="עמוד מכשיר · לשונית עדכונים" icon={Download}>
            <Item name="חלון עדכון אוטומטי">
              מתי הטאבלט יבדוק בעצמו אם יש גרסה חדשה: יומי או שבועי, ביום ובשעה שתקבע. מוצגות גם
              הגרסה המותקנת כרגע ומועד הבדיקה הבאה. כדאי לקבוע שעה שבה לא מתקיים אירוע.
            </Item>
            <Item name="הרץ עדכון עכשיו">
              מבקש מהטאבלט לבדוק מיד מול השרת אם קיימת גרסה חדשה יותר מזו שמותקנת עליו, ואם כן -
              להוריד ולהתקין. אם הוא כבר על הגרסה הנוכחית, לא יקרה כלום.
            </Item>
            <Item name="התקנת גרסה ספציפית">
              דוחף גרסה שבחרת לטאבלט הזה בלבד, גם אם היא זהה למותקנת או ישנה ממנה. מיועד לבדיקה על
              מכשיר אחד לפני שמפיצים לכולם.
            </Item>
            <Item name="חלון המעקב">
              בזמן עדכון או התקנה נפתח חלון שמציג את התקדמות התהליך מהמכשיר בזמן אמת. בסיום ההתקנה
              האפליקציה עולה מחדש, ולכן הטאבלט מתנתק לרגע וחוזר.
            </Item>
          </Section>

          <Section id="device-logs" title="עמוד מכשיר · לשונית יומן" icon={Activity}>
            <Item name="לוגים · שליפת 200 שורות">
              מושך את השורות האחרונות מהלוג של האפליקציה על הטאבלט - צילום מצב חד-פעמי.
            </Item>
            <Item name="לוגים · זרם חי">
              הטאבלט מתחיל לשדר את הלוג שלו בזמן אמת אל המסך הזה. שימושי כשמנסים לשחזר תקלה תוך כדי
              צפייה. כבה אותו כשסיימת - הוא ממשיך לשדר כל עוד הוא דלוק.
            </Item>
            <Item name="היסטוריית אירועים">
              כל מה שקרה למכשיר, כפי שנשמר בשרת: חיבורים, ניתוקים, פקודות שנשלחו והתשובות עליהן.
              לחיצה על שורה פותחת את התוכן המלא של האירוע (ה-JSON), כולל מזהה הקורלציה שמקשר בין
              פקודה לתשובה שלה.
              <br />
              החיפוש מסנן את האירועים שכבר נטענו; "טען עוד" מביא מנה נוספת של 100 אירועים ישנים יותר.
              <br />
              <span className="text-muted-foreground">
                תשאול הכתובת המוצגת (שרץ כל 30 שניות) לא נרשם ביומן בכוונה, כדי לא להציף אותו.
              </span>
            </Item>
          </Section>

          <Section id="builds" title="עמוד הגרסאות" icon={Package}>
            <p>
              מרכז ההפצה של גרסאות האפליקציה. הגרסאות מועלות לכאן פעם אחת, והטאבלטים מושכים מכאן.
            </p>
            <Item name="העלאת APK">
              מעלה קובץ התקנה חדש עם מספר גרסה והערות שחרור. ההעלאה בלבד לא מתקינה כלום על אף טאבלט.
            </Item>
            <Item name="סמן כנוכחית">
              קובע איזו גרסה היא זו שהטאבלטים יתקינו כשהם בודקים עדכון. זה השלב שבו הגרסה באמת יוצאת
              לדרך - עד אליו היא רק שוכבת בשרת.
            </Item>
            <Item name="מתג העדכון האוטומטי הגלובלי">
              מתג כיבוי לכל המערכת: כשהוא כבוי, אף טאבלט לא ימצא גרסה להוריד, גם אם חלון העדכון שלו
              פעיל. כבה אותו בימי אירועים אם אתה רוצה שקט מוחלט.
            </Item>
            <Item name="מחיקת גרסה">
              מסירה את קובץ ההתקנה מהשרת. טאבלטים שכבר התקינו אותה לא מושפעים.
            </Item>
          </Section>

          <Section id="appearance" title="מראה וערכת נושא" icon={Sun}>
            <p>
              בכותרת העמוד יש בורר עם שלוש אפשרויות: <b>בהיר</b>, <b>כהה</b> ו<b>לפי המערכת</b>
              {" "}(מתיישר אוטומטית עם הגדרת מערכת ההפעלה שלך, כולל מעבר יזום לערכה כהה בערב).
              הבחירה נשמרת בדפדפן הזה ותיזכר בפעם הבאה.
            </p>
          </Section>

          <Section id="troubleshooting" title="פתרון בעיות" icon={TriangleAlert}>
            <Trouble problem="הכפתורים במכשיר מנוטרלים">
              הטאבלט לא מחובר כרגע. בדוק חשמל ורשת במקום, וחכה - הוא מנסה להתחבר מחדש מעצמו. פקודות
              לא נשמרות בתור בזמן שהוא מנותק.
            </Trouble>
            <Trouble problem="המכשיר לא ענה בזמן">
              הפקודה נשלחה אבל לא חזרה תשובה תוך 15 שניות. בדרך כלל רשת איטית או טאבלט עמוס. נסה שוב;
              אם זה חוזר, רענן WebView או הפעל את האפליקציה מחדש.
            </Trouble>
            <Trouble problem="מסך האפליקציה אינו רשום לשליטה מרחוק">
              האפליקציה רצה, אבל המסך שלה לא פעיל - ולכן פקודות שנוגעות לתצוגה נכשלות. הפעל את
              האפליקציה מחדש (לשונית הגדרות ← "הפעל מחדש").
            </Trouble>
            <Trouble problem={`"מוצג כעת על המסך" מראה "אין עמוד טעון"`}>
              הטאבלט ענה כרגיל, אבל אין דף פתוח בדפדפן הפנימי שלו. בדוק את כתובת האתחול ואת רשימת
              הדומיינים המורשים, ואז רענן WebView.
            </Trouble>
            <Trouble problem="הטאבלט לא מופיע ברשימה בכלל">
              הוא מעולם לא התחבר לשרת הזה. בדוק בהגדרות השליטה מרחוק שעל הטאבלט שכתובת השרת נכונה.
              אם הטאבלט נמצא מאחורי רשת מסוננת (נטפרי, נטספארק, 019 וכדומה) - ראה הסעיף הבא.
            </Trouble>
            <Trouble problem="הטאבלט מציג SSL Error, או לא מתחבר לשרת ברשת מסוננת">
              רשתות סינון מחליפות את תעודת האבטחה של האתרים, והטאבלט לא מזהה אותה כמהימנה. הטאבלט
              יקפיץ על מסכו דיאלוג עם פרטי התעודה וטביעת אצבע, ואישור שם פותר את זה - האישור נשמר
              לתעודה הזו בלבד, כך שאם התעודה תוחלף תישאל שוב.
              <br />
              לחלופין, בלשונית ההגדרות אפשר להדליק "התעלמות משגיאות SSL" וזה יעבוד מול כל רשת
              מסוננת בלי לשאול.
              <Warn>
                שתי הדרכים מוותרות על האימות שהשרת הוא באמת מי שהוא טוען - הדלק רק ברשת שאתה מכיר.
              </Warn>
            </Trouble>
            <Trouble problem="ביקשתי שיקוף מסך מלא וכלום לא קרה">
              דיאלוג האישור מוצג על מסך הטאבלט וממתין ללחיצה פיזית שם. אין דרך לאשר אותו מרחוק.
              אם אין מי שיגש לטאבלט - השתמש במצב "חלון האפליקציה", שלא דורש אישור כלל.
            </Trouble>
            <Trouble problem="מכשיר מופיע פעמיים">
              לא אמור לקרות - הזיהוי הוא לפי מזהה קבוע של הטאבלט. אם זה קרה, כנראה מדובר בשני
              מכשירים פיזיים שונים; תן להם תוויות שונות כדי להבחין ביניהם.
            </Trouble>
          </Section>

          <p className="text-center text-sm text-muted-foreground">
            לא מצאת תשובה? הכול נרשם ביומן האירועים של המכשיר -{" "}
            <Link to="/" className="underline hover:text-foreground">
              חזרה לרשימת המכשירים
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}

function Section({
  id, title, icon: Icon, children,
}: {
  id: string
  title: string
  icon: React.ElementType
  children: React.ReactNode
}) {
  return (
    // scroll-mt מפצה על הכותרת הדביקה, אחרת קפיצה לעוגן מסתירה את שורת הכותרת
    <Card id={id} className="scroll-mt-24">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed">{children}</CardContent>
    </Card>
  )
}

function Item({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Separator />
      <div className="pt-2 font-medium">{name}</div>
      <div className="text-muted-foreground">{children}</div>
    </div>
  )
}

function Term({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <Badge variant="outline">{term}</Badge>
      <span className="flex-1 text-muted-foreground">{children}</span>
    </div>
  )
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <span className="mt-1 flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
      <span>{children}</span>
    </span>
  )
}

function Trouble({ problem, children }: { problem: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="font-medium">{problem}</div>
      <div className="mt-1 text-muted-foreground">{children}</div>
    </div>
  )
}
